import React, { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import {
    Avatar,
    Badge,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    ListItemIcon,
    Popover,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import Logout from '@mui/icons-material/Logout';
import { performLogout } from '../helpers/sessionHelper';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector } from 'react-redux';
import { orange } from '@mui/material/colors';
import { EXPANDED, COLLAPSED } from './sidebar/sidebarNewStyles';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { usePageTitle } from './PageTitleContext';
import { useNavigate } from 'react-router-dom';
import { useSidebarToggle } from '../common/SidebarToggleContext';
import {
    useGetReleaseNotesQuery,
    useGetReleaseNotesUnreadCountQuery,
    useMarkReleaseNotesSeenMutation,
} from '../redux/commonApiCalls';
import { decodeAuthToken } from '../common/util';
import { ESCALATION_HOME } from '../routes/emsV2Visibility';

// The greeting names the PERSON, not the mapped merchant record — several admin
// accounts share one merchant whose name is another user's (QA BUG-05: Ankit's
// session greeted "Hi, Ajeet"). The JWT fullname is per-account and always fresh.
export function greetingName(userInfo) {
    try {
        const token = userInfo?.accessToken || localStorage.getItem('userToken');
        const fullname = token ? decodeAuthToken(token)?.fullname : null;
        return fullname || userInfo?.merchant?.name || '';
    } catch {
        return userInfo?.merchant?.name || '';
    }
}

const menuPaperProps = {
    sx: {
        borderRadius: 1,
        '& li': {
            px: 2,
            py: 2
        },
        p: 0,
        overflow: 'visible',
        '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
        },
        '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
        },
    },
}

const formatDate = (val) => {
    if (!val) return ''
    const d = new Date(val)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const TYPE_COLOR = {
    feature: { bg: '#eef2ff', fg: '#4f46e5' },
    fix: { bg: '#fef3c7', fg: '#b45309' },
    improvement: { bg: '#dcfce7', fg: '#15803d' },
    breaking: { bg: '#fee2e2', fg: '#b91c1c' },
}

function ReleaseNotesBell() {
    const navigate = useNavigate()
    const [anchorEl, setAnchorEl] = useState(null)

    // RTK Query — cached across components, deduped, no manual refetch on route change.
    const { data: unreadCount = 0 } = useGetReleaseNotesUnreadCountQuery()
    const { data: notesData } = useGetReleaseNotesQuery({ limit: 5 })
    const [markSeen] = useMarkReleaseNotesSeenMutation()

    const items = notesData?.items || []
    const latest = items[0] || null
    const totalCount = notesData?.count ?? items.length

    const handleClick = (e) => setAnchorEl(e.currentTarget)
    const handleClose = () => setAnchorEl(null)

    const goToList = () => {
        handleClose()
        if (unreadCount > 0) markSeen()
        navigate('/whats-new')
    }

    const open = Boolean(anchorEl)
    const type = (latest?.changeType || latest?.type || latest?.changes?.[0]?.type || '').toString()
    const typeColor = TYPE_COLOR[type.toLowerCase()] || { bg: '#eef2ff', fg: '#4f46e5' }

    return (
        <>
            <Tooltip title="What's New">
                <IconButton
                    onClick={handleClick}
                    size='small'
                    sx={{
                        ml: 1,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        backgroundColor: '#fff',
                    }}
                >
                    <Badge
                        badgeContent={unreadCount}
                        color='error'
                        overlap='circular'
                        max={99}
                        invisible={!unreadCount}
                    >
                        <NotificationsNoneOutlinedIcon sx={{ color: '#212b36c2' }} fontSize='small' />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        width: 360,
                        borderRadius: 2,
                        overflow: 'hidden',
                        mt: 1,
                    },
                }}
            >
                <Box sx={{ background: 'linear-gradient(135deg, #5b67d8 0%, #6b46c1 100%)', color: '#fff', p: 2 }}>
                    <Stack direction='row' spacing={1.5} alignItems='center'>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1.5,
                                backgroundColor: 'rgba(255,255,255,0.18)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CampaignOutlinedIcon sx={{ color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant='subtitle1' fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                What's New
                            </Typography>
                            <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.85)' }}>
                                Latest updates &amp; improvements
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                {latest ? (
                    <Box sx={{ p: 2, cursor: 'pointer' }} onClick={goToList}>
                        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1, flexWrap: 'wrap' }}>
                            {latest.version && (
                                <Chip
                                    label={latest.version}
                                    size='small'
                                    variant='outlined'
                                    sx={{ fontFamily: 'monospace', fontSize: 10, height: 20 }}
                                />
                            )}
                            {type && (
                                <Chip
                                    label={type.charAt(0).toUpperCase() + type.slice(1)}
                                    size='small'
                                    sx={{
                                        backgroundColor: typeColor.bg,
                                        color: typeColor.fg,
                                        fontWeight: 700,
                                        fontSize: 10,
                                        height: 20,
                                    }}
                                />
                            )}
                            <Box sx={{ flex: 1 }} />
                            <Typography variant='caption' color='text.secondary'>
                                {formatDate(latest.releaseDate || latest.publishedAt || latest.createdAt)}
                            </Typography>
                        </Stack>
                        <Typography variant='subtitle2' fontWeight={700} color='text.primary' sx={{ mb: 0.5 }}>
                            {latest.title || latest.headline}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}>
                            {latest.summary || latest.description}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant='body2' color='text.secondary'>No release notes available</Typography>
                    </Box>
                )}

                <Box
                    onClick={goToList}
                    sx={{
                        borderTop: '1px solid',
                        borderColor: 'grey.200',
                        backgroundColor: '#fafafa',
                        px: 2,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f3f4f6' },
                    }}
                >
                    <Typography variant='caption' color='text.secondary' fontWeight={600}>
                        {totalCount > 0 ? `${totalCount} ${totalCount === 1 ? 'latest update' : 'latest updates'}` : 'No updates'}
                    </Typography>
                    <Typography variant='caption' fontWeight={700} sx={{ color: '#4f46e5' }}>
                        View all ›
                    </Typography>
                </Box>
            </Popover>
        </>
    )
}

export default function TopBar() {
    const navigate = useNavigate();
    const { user: { userInfo } } = useSelector((state) => state);
    const { pageTitle } = usePageTitle();
    const { sidebarWidth } = useSidebarToggle()
    const [awbSearch, setAwbSearch] = useState('')

    const handleAwbSearch = () => {
        const trimmed = awbSearch.trim()
        if (!trimmed) return
        navigate(`/order-detail/${encodeURIComponent(trimmed)}`)
        setAwbSearch('')
    }

    const handleAwbKeyDown = (e) => {
        if (e.key === 'Enter') handleAwbSearch()
    }

    useEffect(() => {
        window.FreshworksWidget('hide', 'launcher')
    }, []);


    const onLogOut = () => {
        performLogout();
    }

    const openWidget = () => {
        window.FreshworksWidget('open');
        window.FreshworksWidget('identify', 'ticketForm', {
            // name: userInfo?.username,
            email: userInfo?.username,
        }, {
            formId: 88000047445 // Ticket Form ID
        });
        window.FreshworksWidget('prefill', 'ticketForm', {
            group_id: 88000099283,
            type: "Problem",
            custom_fields: {
                cf_brand_name: userInfo?.merchant?.name
            }
        });
    }

    return (
        <AppBar position="fixed" sx={{
            background: '#fff',
            boxShadow: 0,
            borderBottom: "1px solid rgba(145, 158, 171, 0.24)",
            width: {
                md: sidebarWidth ? `calc(100% - ${COLLAPSED}px)` : `calc(100% - ${EXPANDED}px)`
            },
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
            <Toolbar>
                <Typography color="black" variant={'subtitle1'} component="div">
                    {pageTitle}
                </Typography>
                <Box sx={{ flexGrow: 1 }} ></Box>

                <TextField
                    size="small"
                    placeholder="Search AWB"
                    value={awbSearch}
                    onChange={(e) => setAwbSearch(e.target.value)}
                    onKeyDown={handleAwbKeyDown}
                    sx={{
                        width: 240,
                        mr: 2,
                        '& .MuiOutlinedInput-root': {
                            height: 36,
                            backgroundColor: '#f4f6f8',
                            borderRadius: '8px',
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    fontSize="small"
                                    sx={{ cursor: 'pointer', color: 'text.secondary' }}
                                    onClick={handleAwbSearch}
                                />
                            </InputAdornment>
                        ),
                    }}
                />

                <PopupState variant="popover" popupId="profile-menu">
                    {(popupState) => (
                        <React.Fragment>

                            <Button
                                size="small"
                                color='warning'
                                variant='outlined'
                                onClick={() => navigate(ESCALATION_HOME)}
                                sx={{
                                    textTransform: 'capitalize',
                                    color: '#212b36c2',
                                }}>Escalation Management
                            </Button>

                            <Button
                                {...bindTrigger(popupState)}
                                size="small"
                                color='warning'
                                variant='outlined'
                                onClick={() => openWidget()}
                                sx={{
                                    textTransform: 'capitalize',
                                    color: '#212b36c2',
                                    ml: 2
                                }}>Help

                            </Button>

                            <ReleaseNotesBell />

                            <Button
                                {...bindTrigger(popupState)}
                                size="small"
                                sx={{
                                    textTransform: 'capitalize',
                                    color: '#212b36c2',
                                    ml: 2
                                }}
                                endIcon={<Avatar sx={{
                                    backgroundColor: orange[300],
                                    borderRadius: '5px',
                                    height: 25,
                                    width: 25
                                }}>{greetingName(userInfo)?.charAt(0)}</Avatar>}
                            >
                                Hi, {greetingName(userInfo).split(' ').shift()}
                            </Button>
                            <Menu
                                {...bindMenu(popupState)}
                                keepMounted
                                PaperProps={menuPaperProps}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            >

                                <MenuItem divider sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography fontWeight={500}>{greetingName(userInfo)}</Typography>
                                    <Typography variant='body2'>{userInfo?.username}</Typography>
                                </MenuItem>
                                <MenuItem onClick={onLogOut}>
                                    <ListItemIcon>
                                        <Logout fontSize="small" />
                                    </ListItemIcon>
                                    Logout
                                </MenuItem>

                            </Menu>
                        </React.Fragment>
                    )}
                </PopupState>
            </Toolbar>
        </AppBar>
    );
}
